import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import EventIcon from '@mui/icons-material/Event';
import type { ParishLandingEventsSortMode } from '../../services/parishLandingService';

export type ShowCatalogItem = {
  id: string;
  title: string;
  status: string;
  coverImage?: string;
};

type FilterMode = 'all' | 'on' | 'off';

type Props = {
  open: boolean;
  onClose: () => void;
  catalog: ShowCatalogItem[];
  eventIds: string[];
  sortMode: ParishLandingEventsSortMode;
  onApply: (next: { eventIds: string[]; sortMode: ParishLandingEventsSortMode }) => void;
};

const PLACEHOLDER_IMG =
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200"><rect fill="#e5e7eb" width="400" height="200"/><text x="50%" y="50%" fill="#9ca3af" font-family="sans-serif" font-size="16" text-anchor="middle" dy=".3em">Sem capa</text></svg>`
  );

/** Portal evita o transform do Dialog do MUI quebrar o drag do hello-pangea. */
function PortalDraggableCard({
  provided,
  snapshot,
  children,
}: {
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  children: React.ReactNode;
}) {
  const style = {
    ...provided.draggableProps.style,
    ...(snapshot.isDragging
      ? {
          // No body o % do Grid quebra; trava largura de card no drag.
          width: 300,
          zIndex: 1400,
        }
      : null),
  };
  const node = (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={style}
      sx={{ display: 'flex' }}
    >
      {children}
    </Grid>
  );
  if (!snapshot.isDragging) return node;
  return createPortal(node, document.body);
}

const ZuadariaShowsOrderDialog: React.FC<Props> = ({
  open,
  onClose,
  catalog,
  eventIds,
  sortMode,
  onApply,
}) => {
  const [draftIds, setDraftIds] = useState<string[]>(eventIds);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('on');
  const [readyToDrag, setReadyToDrag] = useState(false);

  useEffect(() => {
    if (!open) {
      setReadyToDrag(false);
      return;
    }
    setDraftIds(eventIds);
    setSearch('');
    setFilter(sortMode === 'custom' ? 'on' : 'all');
    // Espera a transição do Dialog (transform) terminar antes de habilitar drag.
    const t = window.setTimeout(() => setReadyToDrag(true), 220);
    return () => window.clearTimeout(t);
  }, [open, eventIds, sortMode]);

  const selectedSet = useMemo(() => new Set(draftIds), [draftIds]);
  const byId = useMemo(() => new Map(catalog.map((e) => [e.id, e])), [catalog]);

  const orderedOnPage = useMemo(
    () => draftIds.map((id) => byId.get(id)).filter(Boolean) as ShowCatalogItem[],
    [draftIds, byId]
  );

  const gridItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = (ev: ShowCatalogItem) =>
      !term || ev.title.toLowerCase().includes(term) || ev.status.toLowerCase().includes(term);

    if (filter === 'on') return orderedOnPage.filter(matches);
    if (filter === 'off') {
      const on = new Set(draftIds);
      return catalog.filter((e) => !on.has(e.id) && matches(e));
    }
    // all: selecionados na ordem + fora
    const onSet = new Set(draftIds);
    const rest = catalog.filter((e) => !onSet.has(e.id) && matches(e));
    return [...orderedOnPage.filter(matches), ...rest];
  }, [filter, orderedOnPage, catalog, draftIds, search]);

  const toggle = (id: string) => {
    setDraftIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const moveInSelected = (id: string, toIndex: number) => {
    setDraftIds((prev) => {
      const ids = prev.filter((x) => x !== id);
      ids.splice(Math.max(0, Math.min(toIndex, ids.length)), 0, id);
      return ids;
    });
  };

  /** Drag só com “Na página” + sem busca — índices batem com draftIds no catálogo. */
  const canDrag = readyToDrag && sortMode === 'custom' && filter === 'on' && !search.trim();

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !canDrag) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const movedId = gridItems[from]?.id;
    if (!movedId) return;
    setDraftIds((prev) => {
      const inCatalog = new Set(catalog.map((c) => c.id));
      const ordered = prev.filter((id) => inCatalog.has(id));
      const orphans = prev.filter((id) => !inCatalog.has(id));
      const next = [...ordered];
      const fromIdx = next.indexOf(movedId);
      if (fromIdx < 0) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
      return [...next, ...orphans];
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      // Focus trap + transição com transform quebram o hello-pangea dentro do Dialog
      disableEnforceFocus
      disableAutoFocus
      transitionDuration={0}
      PaperProps={{ sx: { transform: 'none !important' } }}
    >
      <DialogTitle>Organizar shows</DialogTitle>
      <DialogContent dividers sx={{ overflowX: 'hidden' }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {sortMode === 'custom'
              ? 'Mesma grade do admin (App para App): arraste pelo ícone ≡. Arrastar libera no filtro “Na página”, sem busca.'
              : 'Marque quais shows entram. A ordem automática segue a opção escolhida na página.'}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar show…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={filter}
              onChange={(_, value) => value && setFilter(value)}
            >
              <ToggleButton value="all">Todos ({catalog.length})</ToggleButton>
              <ToggleButton value="on">Na página ({draftIds.length})</ToggleButton>
              <ToggleButton value="off">
                Fora ({Math.max(0, catalog.length - draftIds.length)})
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {sortMode === 'custom' && !canDrag && (
            <Typography variant="caption" color="warning.main">
              Para arrastar: filtro “Na página” e limpe a busca.
            </Typography>
          )}

          {gridItems.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
              <EventIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography color="text.secondary">Nenhum show neste filtro.</Typography>
            </Box>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="zuadaria-shows-grid" isDropDisabled={!canDrag}>
                {(provided) => (
                  <Grid
                    container
                    spacing={2}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ minHeight: 200 }}
                  >
                    {gridItems.map((ev, index) => {
                      const onPage = selectedSet.has(ev.id);
                      const orderIndex = onPage ? draftIds.indexOf(ev.id) : -1;
                      return (
                        <Draggable
                          key={ev.id}
                          draggableId={ev.id}
                          index={index}
                          isDragDisabled={!canDrag || !onPage}
                        >
                          {(dragProvided, snapshot) => (
                            <PortalDraggableCard provided={dragProvided} snapshot={snapshot}>
                              <Card
                                variant="outlined"
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  transition: 'box-shadow 0.2s',
                                  boxShadow: snapshot.isDragging ? 4 : 0,
                                  opacity: snapshot.isDragging ? 0.9 : onPage ? 1 : 0.72,
                                  '&:hover': { boxShadow: 2 },
                                }}
                              >
                                <Box
                                  {...dragProvided.dragHandleProps}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1,
                                    py: 0.5,
                                    bgcolor: 'action.hover',
                                    cursor: canDrag && onPage ? 'grab' : 'default',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <DragIndicatorIcon
                                    fontSize="small"
                                    color={canDrag && onPage ? 'action' : 'disabled'}
                                  />
                                  {onPage && sortMode === 'custom' && (
                                    <Chip size="small" label={`#${orderIndex + 1}`} sx={{ height: 22 }} />
                                  )}
                                  <Box sx={{ flex: 1 }} />
                                  {sortMode === 'custom' && onPage && (
                                    <>
                                      <Tooltip title="Topo">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={orderIndex <= 0}
                                            onClick={() => moveInSelected(ev.id, 0)}
                                          >
                                            <VerticalAlignTopIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Fim">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={orderIndex >= draftIds.length - 1}
                                            onClick={() => moveInSelected(ev.id, draftIds.length - 1)}
                                          >
                                            <VerticalAlignBottomIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                                <CardMedia
                                  component="img"
                                  height="140"
                                  image={ev.coverImage || PLACEHOLDER_IMG}
                                  alt={ev.title}
                                  sx={{ objectFit: 'cover' }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = PLACEHOLDER_IMG;
                                  }}
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    {ev.title}
                                  </Typography>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={onPage}
                                        onChange={() => toggle(ev.id)}
                                      />
                                    }
                                    label={
                                      <Typography variant="body2">
                                        {onPage ? 'Na página' : 'Incluir na página'}
                                      </Typography>
                                    }
                                  />
                                </CardContent>
                              </Card>
                            </PortalDraggableCard>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={() => {
            onApply({ eventIds: draftIds, sortMode });
            onClose();
          }}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZuadariaShowsOrderDialog;
